import {
  Alert,
  focusProps,
  Icons,
  Typography,
  type SxProps,
} from '@cast/design-system';

import { raiseReportingMonetizationEvent } from './analytics/reportingMonetizationEvents';
import { useReportingTrialExplainDialog } from './dialogs/useReportingTrialExplainDialog';
import type { ReportingTrialFleet } from './reportingPlans';
import { useReportingTrial } from './useReportingTrial';

type ReportingTrialConnectionAlertProps = {
  fleet?: ReportingTrialFleet;
  onViewPlans?: () => void;
  onContactSales?: () => void;
  sx?: SxProps;
};

/**
 * Pre-trial empty-state / connect-flow nudge (Figma Alert 27:45038 / 27:56180).
 * Visible only when the org is eligible and the reporting trial has not started.
 */
export const ReportingTrialConnectionAlert = ({
  fleet,
  onViewPlans,
  onContactSales,
  sx,
}: ReportingTrialConnectionAlertProps) => {
  const { isPreTrial } = useReportingTrial();
  const { openDialog } = useReportingTrialExplainDialog();

  if (!isPreTrial) {
    return null;
  }

  const handleLearnMore = () => {
    raiseReportingMonetizationEvent('reporting_offer_viewed', {
      source: 'connection_trial_alert',
    });
    openDialog({ fleet, onViewPlans, onContactSales });
  };

  return (
    <Alert
      color="advisory"
      size="sm"
      icon={<Icons.Info weight="light" />}
      testId="reporting-trial-connection-alert"
      sx={sx}
    >
      <Typography variant="P12R" component="span">
        Connection starts a 30-day trial. No card required.{' '}
        <Typography
          component="button"
          type="button"
          variant="P12R"
          onClick={handleLearnMore}
          data-testid="reporting-trial-connection-alert-learn-more"
          sx={{
            all: 'unset',
            cursor: 'pointer',
            textDecoration: 'underline',
            textUnderlinePosition: 'from-font',
            // `all: 'unset'` removes the default focus outline — restore a
            // visible keyboard focus indicator (matches DS focusProps).
            '&:focus-visible': focusProps(),
          }}
        >
          Learn more
        </Typography>
      </Typography>
    </Alert>
  );
};
